import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Sparkles, Quote, Star, Loader2, Crown, Check } from "lucide-react";
import { api, imgUrl } from "@/lib/api";
import { usePageMeta } from "@/hooks/usePageMeta";

function PickCard({ pick, code }) {
  const navigate = useNavigate();
  const p = pick.product;
  const onSale = p.sale_price && p.sale_price < p.price;
  const price = onSale ? p.sale_price : p.price;
  const goToProduct = () => navigate(`/product/${p.id}?ref=${code}`);

  return (
    <div
      data-testid={`creator-pick-${pick.id}`}
      className="bg-white border border-neutral-200 rounded-2xl overflow-hidden hover:border-[#E8956B]/60 hover:shadow-[0_14px_34px_rgba(0,0,0,0.08)] transition-all duration-300 flex flex-col"
    >
      <div onClick={goToProduct} className="relative aspect-square overflow-hidden bg-neutral-100 cursor-pointer group">
        <img src={imgUrl(p.images?.[0])} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
        <span className="absolute top-3 left-3 bg-[#E8956B] text-white text-[10px] font-bold px-2.5 py-1 rounded-full tracking-wider flex items-center gap-1">
          <Star size={11} fill="currentColor" /> PICK
        </span>
      </div>
      <div className="p-4 flex flex-col flex-1">
        {pick.note && (
          <p className="text-sm text-neutral-600 italic leading-relaxed flex gap-1.5">
            <Quote size={14} className="text-[#E8956B] shrink-0 mt-0.5" />
            <span className="line-clamp-3">{pick.note}</span>
          </p>
        )}
        <div className="mt-3">
          <h3 className="font-semibold text-neutral-900 text-sm leading-tight line-clamp-1">{p.title}</h3>
          <p className="text-xs text-neutral-500">{p.brand}</p>
        </div>
        <div className="mt-2 flex items-center gap-2">
          <span className="text-lg font-bold text-neutral-900">${price.toFixed(2)}</span>
          {onSale && <span className="text-sm text-neutral-400 line-through">${p.price.toFixed(2)}</span>}
        </div>
        <button
          data-testid={`creator-shop-pick-${pick.id}`}
          onClick={goToProduct}
          className="mt-3 w-full bg-[#E8956B] hover:bg-[#CE744A] text-white font-bold rounded-full py-2 text-sm btn-glow transition-all"
        >
          Shop This Pick
        </button>
      </div>
    </div>
  );
}

const VIP_PERKS = ["Members-only coupons", "Early access to drops", "Monthly beauty newsletter", "Gold VIP badge"];

export default function CreatorPage() {
  const { code } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);

  const name = data?.influencer?.name;
  usePageMeta(
    name ? `${name}'s Picks & VIP Membership | Beauty Trade Club` : "Creator Picks | Beauty Trade Club",
    name ? `Shop ${name}'s hand-picked beauty finds and join VIP through their page on Beauty Trade Club.` : "Shop creator-curated beauty picks on Beauty Trade Club.",
    `/creator/${code}`
  );

  useEffect(() => {
    setData(null); setNotFound(false);
    api.get(`/creator/${code}`).then(({ data }) => setData(data)).catch(() => setNotFound(true));
  }, [code]);

  if (notFound) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-24 text-center" data-testid="creator-not-found">
        <h1 className="font-display text-3xl font-black">Creator not found</h1>
        <p className="text-neutral-500 mt-3">This creator link doesn't exist or is no longer active.</p>
        <button onClick={() => navigate("/picks")} className="mt-6 text-[#E8956B] font-semibold underline">Browse all picks</button>
      </div>
    );
  }

  if (!data) return <div className="flex justify-center py-32"><Loader2 className="animate-spin text-[#E8956B]" /></div>;

  const inf = data.influencer;

  return (
    <div className="max-w-6xl mx-auto px-6 py-12" data-testid="creator-page">
      {/* Creator header */}
      <div className="text-center mb-10">
        <div className="w-20 h-20 mx-auto rounded-full bg-[#E8956B] text-white flex items-center justify-center text-3xl font-black shadow-lg">
          {inf.avatar ? <img src={imgUrl(inf.avatar)} alt={inf.name} className="w-full h-full rounded-full object-cover" /> : inf.name?.[0]?.toUpperCase()}
        </div>
        <div className="flex items-center justify-center gap-2 text-[#E8956B] text-xs font-extrabold uppercase tracking-[0.3em] mt-4">
          <Sparkles size={14} /> Verified Creator
        </div>
        <h1 data-testid="creator-name" className="font-display text-4xl sm:text-5xl font-black text-neutral-900 mt-2">{inf.name}</h1>
        <p className="mt-3 text-neutral-500 max-w-xl mx-auto">Shop {inf.name}'s favorite beauty finds — every pick is a genuine recommendation. Buy through this page to support the creator.</p>
      </div>

      {/* VIP membership CTA */}
      <div data-testid="creator-vip-cta" className="bg-gradient-to-br from-neutral-900 to-neutral-800 text-white rounded-3xl p-8 mb-12 max-w-3xl mx-auto shadow-xl">
        <div className="flex flex-col sm:flex-row items-center gap-6">
          <div className="flex-1">
            <div className="flex items-center gap-2 text-[#E8956B] font-bold uppercase tracking-[0.2em] text-xs mb-2">
              <Crown size={16} /> Beauty Trade Club VIP
            </div>
            <h2 className="font-display text-2xl font-black">Join VIP with {inf.name}</h2>
            <p className="text-neutral-300 text-sm mt-2">Unlock exclusive perks for <b className="text-white">$4.99/month</b> and support {inf.name} when you join through their page.</p>
            <div className="mt-4 grid grid-cols-2 gap-x-4 gap-y-2">
              {VIP_PERKS.map((perk) => (
                <div key={perk} className="flex items-center gap-2 text-sm text-neutral-200">
                  <Check size={14} className="text-[#E8956B] shrink-0" /> {perk}
                </div>
              ))}
            </div>
          </div>
          <button
            data-testid="creator-join-vip-btn"
            onClick={() => navigate(`/vip?ref=${inf.influencer_code}`)}
            className="bg-[#E8956B] hover:bg-[#CE744A] text-white font-bold rounded-full px-8 py-4 text-lg btn-glow transition-all inline-flex items-center gap-2 shrink-0"
          >
            <Crown size={20} /> Join VIP
          </button>
        </div>
      </div>

      {/* Picks */}
      <h2 className="font-display text-2xl font-bold text-neutral-900 mb-5">{inf.name}'s Picks</h2>
      {data.picks.length === 0 ? (
        <p className="text-center text-neutral-400 py-16" data-testid="creator-no-picks">No picks yet — check back soon! ✨</p>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5" data-testid="creator-picks-grid">
          {data.picks.map((pick) => <PickCard key={pick.id} pick={pick} code={inf.influencer_code} />)}
        </div>
      )}
    </div>
  );
}
